import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';

const COLUMNS = [
  { key: 'name', header: 'Name', sortKey: 'name' },
  { key: 'mobile', header: 'Mobile', sortKey: 'mobile' },
  { key: 'city', header: 'City', render: (r) => r.city || '—' },
  { key: 'gstin', header: 'GSTIN', render: (r) => r.gstin || '—' },
  {
    key: 'customer_type',
    header: 'Type',
    render: (r) => (r.customer_type === 'wholesale' ? 'Wholesale' : 'Retail'),
  },
];

const EMPTY_FORM = {
  name: '',
  mobile: '',
  email: '',
  addressLine1: '',
  city: '',
  state: '',
  pincode: '',
  gstin: '',
  customerType: 'retail',
  notes: '',
};

export default function CustomersPage() {
  const [query, onQueryChange] = useListQuery();
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const listQuery = useQuery({
    queryKey: ['customers', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/customers', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...EMPTY_FORM, ...row, gstin: row.gstin ?? '', customerType: row.customer_type });
    setFieldErrors({});
    setFormError(null);
    setModalOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      editing ? api.patch(`/customers/${editing.id}`, form) : api.post('/customers', form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setModalOpen(false);
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.details) {
        setFieldErrors(err.details);
      } else {
        setFormError(err instanceof ApiClientError ? err.message : 'Could not save. Please try again.');
      }
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    saveMutation.mutate();
  };

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/customers/${editing.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setConfirmDeleteOpen(false);
      setModalOpen(false);
    },
    onError: (err) => setDeleteError(err instanceof ApiClientError ? err.message : 'Could not delete this customer.'),
  });

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Customers</h1>
        <Button onClick={openCreate}>
          <Plus size={17} aria-hidden="true" /> Add
        </Button>
      </div>

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        onRowClick={openEdit}
        searchPlaceholder="Search by name, mobile or GSTIN…"
        emptyLabel="No customers yet. Add the first one to get started."
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit customer' : 'Add customer'}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <TextField label="Name" required value={form.name} onChange={setField('name')} error={fieldErrors.name} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Mobile" inputMode="numeric" value={form.mobile} onChange={setField('mobile')} error={fieldErrors.mobile} />
            <SelectField label="Type" value={form.customerType} onChange={setField('customerType')}>
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
            </SelectField>
          </div>
          <TextField label="Address" value={form.addressLine1} onChange={setField('addressLine1')} error={fieldErrors.addressLine1} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="City" value={form.city} onChange={setField('city')} />
            <TextField label="PIN code" inputMode="numeric" value={form.pincode} onChange={setField('pincode')} error={fieldErrors.pincode} />
          </div>
          <TextField
            label="GSTIN"
            value={form.gstin}
            onChange={setField('gstin')}
            error={fieldErrors.gstin}
            hint="Leave blank for a walk-in (B2C) customer"
          />
          <TextAreaField label="Notes" value={form.notes} onChange={setField('notes')} />

          {formError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {formError}
            </p>
          ) : null}

          <div className="mt-2 flex items-center justify-between gap-3">
            {editing ? (
              <button
                type="button"
                onClick={() => { setDeleteError(null); setConfirmDeleteOpen(true); }}
                className="flex items-center gap-1.5 text-sm text-danger hover:underline"
              >
                <Trash2 size={15} aria-hidden="true" /> Delete
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={saveMutation.isPending}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} title="Delete this customer?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            <b>{editing?.name}</b> will no longer appear in customer lookups. Any invoices already issued to them keep
            their own copy of the name and address, so past bills are unaffected.
          </p>
          {deleteError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {deleteError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setConfirmDeleteOpen(false)}>
              Keep customer
            </Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              <Trash2 size={16} aria-hidden="true" /> Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
